import React from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';

import { platformFeatureFlagsService } from '../../services/api';
import { usePlatformActionPermissions } from '../../hooks/usePlatformActionPermissions';

const { Title, Text } = Typography;

const SCOPE_OPTIONS = [
  { label: 'GLOBAL', value: 'GLOBAL' },
  { label: 'TENANT', value: 'TENANT' },
];

const INITIAL_FLAGS = [
  'saas.billing.subscription_change_auto_apply',
  'saas.billing.retry_engine_enabled',
  'saas.billing.replay_enabled',
  'saas.billing.hard_enforcement_enabled',
  'saas.billing.provider_write_enabled',
];

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const PERMISSION_TOOLTIP_MESSAGE = 'Você não possui permissão para executar esta ação';

const withPermissionTooltip = (node, disabledByPermission) => {
  if (!disabledByPermission) {
    return node;
  }

  return (
    <Tooltip title={PERMISSION_TOOLTIP_MESSAGE}>
      <span style={{ display: 'inline-block' }}>{node}</span>
    </Tooltip>
  );
};

const PlatformFeatureFlagsPage = () => {
  const { canExecuteAction } = usePlatformActionPermissions();
  const [filters, setFilters] = React.useState({
    flag_key: undefined,
    scope: undefined,
    tenant_id: '',
    enabled: undefined,
    environment: '',
    limit: 200,
  });
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [upsertLoading, setUpsertLoading] = React.useState(false);
  const [upsertForm] = Form.useForm();

  const [resolveForm] = Form.useForm();
  const [resolveResult, setResolveResult] = React.useState(null);
  const [resolveLoading, setResolveLoading] = React.useState(false);

  const fetchFlags = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await platformFeatureFlagsService.list({
        flag_key: filters.flag_key || undefined,
        scope: filters.scope || undefined,
        tenant_id: (filters.tenant_id || '').trim() || undefined,
        enabled: typeof filters.enabled === 'boolean' ? filters.enabled : undefined,
        environment: (filters.environment || '').trim() || undefined,
        limit: Number(filters.limit) || 200,
      });
      setItems(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      setItems([]);
      setError(getErrorMessage(requestError, 'Nao foi possivel carregar feature flags.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const submitUpsert = async () => {
    try {
      const values = await upsertForm.validateFields();
      setUpsertLoading(true);
      setError('');
      await platformFeatureFlagsService.createOrUpdate({
        flag_key: values.flag_key,
        name: values.name,
        description: values.description || undefined,
        scope: values.scope,
        tenant_id: values.scope === 'TENANT' ? (values.tenant_id || '').trim() || undefined : null,
        enabled: Boolean(values.enabled),
        rollout_percentage: typeof values.rollout_percentage === 'number' ? values.rollout_percentage : null,
        environment: (values.environment || '').trim() || undefined,
        metadata_json: {},
      });
      setUpsertOpen(false);
      upsertForm.resetFields();
      await fetchFlags();
    } catch (requestError) {
      if (requestError?.errorFields) return;
      setError(getErrorMessage(requestError, 'Falha ao criar/atualizar feature flag.'));
    } finally {
      setUpsertLoading(false);
    }
  };

  const toggleFlag = async (row, enabled) => {
    setError('');
    try {
      await platformFeatureFlagsService.setEnabled(row.id, { enabled });
      await fetchFlags();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Falha ao alterar enabled da flag.'));
    }
  };

  const resolveFlag = async () => {
    try {
      const values = await resolveForm.validateFields();
      setResolveLoading(true);
      setError('');
      const response = await platformFeatureFlagsService.resolve({
        flag_key: values.flag_key,
        tenant_id: (values.tenant_id || '').trim() || undefined,
        default: Boolean(values.default || false),
      });
      setResolveResult(response?.data || null);
    } catch (requestError) {
      if (requestError?.errorFields) return;
      setResolveResult(null);
      setError(getErrorMessage(requestError, 'Falha ao resolver flag.'));
    } finally {
      setResolveLoading(false);
    }
  };

  const columns = [
    {
      title: 'Flag Key',
      dataIndex: 'flag_key',
      key: 'flag_key',
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'Escopo',
      dataIndex: 'scope',
      key: 'scope',
      width: 110,
      render: (value) => <Tag color={value === 'TENANT' ? 'gold' : 'blue'}>{value}</Tag>,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 220,
      render: (value) => value || '-',
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      render: (value, row) => {
        const canToggle = canExecuteAction('feature_flags.toggle');
        return withPermissionTooltip(
          <Switch checked={Boolean(value)} onChange={(checked) => toggleFlag(row, checked)} disabled={!canToggle} />,
          !canToggle
        );
      },
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      key: 'environment',
      width: 130,
      render: (value) => value || '-',
    },
    {
      title: 'Rollout %',
      dataIndex: 'rollout_percentage',
      key: 'rollout_percentage',
      width: 120,
      render: (value) => (value === null || value === undefined ? '-' : value),
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 220,
      render: (value) => value || '-',
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value) => formatDateTime(value),
    },
  ];

  const canWrite = canExecuteAction('feature_flags.write');
  const canResolve = canExecuteAction('feature_flags.resolve');

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Feature Flags</Title>
      <Text type="secondary">
        Flags apenas controlam disponibilidade futura; nesta etapa nao executam automacoes.
      </Text>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Falha no modulo de feature flags"
          description={error}
        />
      ) : null}

      <Card size="small" title="Filtros" style={{ marginTop: 16 }}>
        <Space wrap>
          <Select
            placeholder="flag_key"
            options={INITIAL_FLAGS.map((flagKey) => ({ label: flagKey, value: flagKey }))}
            value={filters.flag_key}
            onChange={(value) => setFilters((prev) => ({ ...prev, flag_key: value }))}
            allowClear
            style={{ width: 320 }}
          />
          <Select
            placeholder="scope"
            options={SCOPE_OPTIONS}
            value={filters.scope}
            onChange={(value) => setFilters((prev) => ({ ...prev, scope: value }))}
            allowClear
            style={{ width: 140 }}
          />
          <Input
            placeholder="tenant_id"
            value={filters.tenant_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, tenant_id: event.target.value }))}
            style={{ width: 220 }}
          />
          <Select
            placeholder="enabled"
            value={filters.enabled}
            onChange={(value) => setFilters((prev) => ({ ...prev, enabled: value }))}
            allowClear
            options={[
              { label: 'true', value: true },
              { label: 'false', value: false },
            ]}
            style={{ width: 120 }}
          />
          <Input
            placeholder="environment"
            value={filters.environment}
            onChange={(event) => setFilters((prev) => ({ ...prev, environment: event.target.value }))}
            style={{ width: 150 }}
          />
          <Input
            type="number"
            min={1}
            max={500}
            value={filters.limit}
            onChange={(event) => setFilters((prev) => ({ ...prev, limit: event.target.value }))}
            style={{ width: 100 }}
          />
          <Button type="primary" onClick={fetchFlags} loading={loading}>Aplicar</Button>
          {withPermissionTooltip(
            <Button onClick={() => setUpsertOpen(true)} disabled={!canWrite}>Criar / Atualizar</Button>,
            !canWrite
          )}
        </Space>
      </Card>

      <Card size="small" title={`Flags (${items.length})`} style={{ marginTop: 16 }}>
        <Table
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Card size="small" title="Resolver flag" style={{ marginTop: 16 }}>
        <Space align="start" wrap>
          <Form form={resolveForm} layout="inline">
            <Form.Item name="flag_key" rules={[{ required: true, message: 'flag_key obrigatorio' }]}>
              <Select
                placeholder="flag_key"
                options={INITIAL_FLAGS.map((flagKey) => ({ label: flagKey, value: flagKey }))}
                style={{ width: 340 }}
              />
            </Form.Item>
            <Form.Item name="tenant_id">
              <Input placeholder="tenant_id (opcional)" style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="default" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="default=true" unCheckedChildren="default=false" />
            </Form.Item>
            <Form.Item>
              {withPermissionTooltip(
                <Button type="primary" onClick={resolveFlag} loading={resolveLoading} disabled={!canResolve}>Resolver</Button>,
                !canResolve
              )}
            </Form.Item>
          </Form>
        </Space>
        {resolveResult ? (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={`Resultado: ${resolveResult.enabled ? 'enabled' : 'disabled'} (${resolveResult.resolved_scope})`}
            description={`flag_key=${resolveResult.flag_key}; tenant_id=${resolveResult.tenant_id || '-'}; source_flag_id=${resolveResult.source_flag_id || '-'}`}
          />
        ) : null}
      </Card>

      <Modal
        title="Criar / Atualizar Feature Flag"
        open={upsertOpen}
        onCancel={() => setUpsertOpen(false)}
        onOk={submitUpsert}
        confirmLoading={upsertLoading}
        okButtonProps={{ disabled: !canWrite }}
      >
        <Form
          form={upsertForm}
          layout="vertical"
          initialValues={{
            flag_key: INITIAL_FLAGS[0],
            name: 'Feature flag',
            scope: 'GLOBAL',
            enabled: false,
          }}
        >
          <Form.Item name="flag_key" label="Flag Key" rules={[{ required: true, message: 'flag_key obrigatorio' }]}>
            <Select options={INITIAL_FLAGS.map((flagKey) => ({ label: flagKey, value: flagKey }))} />
          </Form.Item>
          <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'nome obrigatorio' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descricao">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="scope" label="Scope" rules={[{ required: true, message: 'scope obrigatorio' }]}>
            <Select options={SCOPE_OPTIONS} />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, next) => prev.scope !== next.scope} noStyle>
            {({ getFieldValue }) => (
              getFieldValue('scope') === 'TENANT' ? (
                <Form.Item
                  name="tenant_id"
                  label="Tenant ID"
                  rules={[{ required: true, message: 'tenant_id obrigatorio para scope TENANT' }]}
                >
                  <Input />
                </Form.Item>
              ) : null
            )}
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="rollout_percentage" label="Rollout Percentage (0..100)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="environment" label="Environment">
            <Input placeholder="dev/staging/prod" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlatformFeatureFlagsPage;
